// ui/Toast.js
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

/**
 * Enhanced toast notification component
 *
 * @param {boolean} show - Controls toast visibility
 * @param {function} onClose - Called when toast is dismissed
 * @param {string} message - Toast message text
 * @param {string} variant - Toast style: "success", "error", "warning", "critical", "info"
 * @param {number} duration - Auto-dismiss duration in ms (default: 3000, set to 0 to disable)
 * @param {function} [onRetry] - Optional callback for retry action (shows "Try Again" button)
 * @param {string} [retryLabel] - Custom label for retry button (default: "Try Again")
 */
export default function Toast({
  show,
  onClose,
  message = "Action completed",
  variant = "success",
  duration = 3000,
  onRetry,
  retryLabel = "Try Again",
}) {
  // Auto-dismiss after duration
  useEffect(() => {
    if (show && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [show, duration, onClose]);

  if (!show) return null;

  // Variant styles
  const variants = {
    success: {
      icon: CheckCircleIcon,
      iconColor: "text-success",
      bgColor: "bg-surface-darker",
      textColor: "text-text-inverse",
    },
    error: {
      // Amber for recoverable errors
      icon: ExclamationTriangleIcon,
      iconColor: "text-amber-400",
      bgColor: "bg-surface-darker",
      textColor: "text-text-inverse",
    },
    warning: {
      icon: ExclamationTriangleIcon,
      iconColor: "text-amber-400",
      bgColor: "bg-surface-darker",
      textColor: "text-text-inverse",
    },
    critical: {
      // Red for critical/destructive errors
      icon: XCircleIcon,
      iconColor: "text-destructive",
      bgColor: "bg-surface-darker",
      textColor: "text-text-inverse",
    },
    info: {
      icon: InformationCircleIcon,
      iconColor: "text-info",
      bgColor: "bg-surface-darker",
      textColor: "text-text-inverse",
    },
  };

  const config = variants[variant] || variants.success;
  const Icon = config.icon;

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
      onClose();
    }
  };

  const toast = (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 md:translate-x-0 md:top-auto md:left-auto md:bottom-4 md:right-4 z-50 animate-fade-in">
      <div
        className={`rounded-lg ${config.bgColor} ${config.textColor} text-sm px-4 py-3 shadow-lg flex items-center gap-3 min-w-0 w-full max-w-md`}
        role="alert"
        aria-live="polite"
      >
        <Icon className={`h-5 w-5 ${config.iconColor} flex-shrink-0`} />
        <span className="flex-1">{message}</span>

        {onRetry && (
          <button
            onClick={handleRetry}
            className="px-3 py-1 text-xs font-medium rounded-md bg-white/10 hover:bg-white/20 transition-colors flex-shrink-0"
          >
            {retryLabel}
          </button>
        )}

        <button
          onClick={onClose}
          className="p-0.5 rounded hover:bg-white/10 transition-colors flex-shrink-0"
          aria-label="Dismiss"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );

  return createPortal(toast, document.body);
}
